/**
 * Hook System Overview
 * Gestion des données système avec TanStack Query
 *
 * Architecture: Component → Hook (TanStack Query) → Service → API
 * ✅ Utilise useQuery pour données serveur
 * ✅ Récupère la société de l'utilisateur connecté (pas endpoint SuperAdmin)
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usersAPI, authAPI, companiesAPI } from '@/utils/api';
import { useCompanyStore } from '@/stores/companyStore';
import { logger } from '@/utils/logger';

// ============================================================
// TYPES
// ============================================================
export interface SystemUser {
  id: string;
  email: string;
  fullName?: string;
  role?: string;
  createdAt: string;
  lastLoginAt?: string;
}

export interface SystemCompany {
  id: string;
  name: string;
  siret?: string;
  createdAt: string;
  usersCount?: number;
}

export interface SystemStats {
  users: SystemUser[];
  companies: SystemCompany[];
  totalUsers: number;
  totalCompanies: number;
  platformVersion: string;
}

// ============================================================
// QUERY KEYS
// ============================================================
export const systemOverviewKeys = {
  all: ['system-overview'] as const,
  stats: () => [...systemOverviewKeys.all, 'stats'] as const,
};

// ============================================================
// HOOK
// ============================================================
export function useSystemOverview() {
  const queryClient = useQueryClient();
  const { company: storeCompany } = useCompanyStore();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: systemOverviewKeys.stats(),
    queryFn: async (): Promise<SystemStats> => {
      // 1. Récupérer les utilisateurs de la société
      let usersArray: SystemUser[] = [];
      try {
        const usersData = await usersAPI.getAll();
        // Parse différents formats de réponse
        if (Array.isArray(usersData)) {
          usersArray = usersData;
        } else if (usersData?.data && Array.isArray(usersData.data)) {
          usersArray = usersData.data;
        } else if (usersData?.users && Array.isArray(usersData.users)) {
          usersArray = usersData.users;
        } else if (usersData?.data?.users && Array.isArray(usersData.data.users)) {
          usersArray = usersData.data.users;
        }
      } catch (err) {
        logger.warn('Erreur récupération utilisateurs:', err);
      }

      // 2. Récupérer la société de l'utilisateur connecté
      let companiesArray: SystemCompany[] = [];
      try {
        // D'abord essayer le store
        if (storeCompany) {
          companiesArray = [{
            id: storeCompany.id,
            name: storeCompany.tradeName || storeCompany.legalName || 'Ma société',
            siret: storeCompany.registrationNumber,
            createdAt: typeof storeCompany.createdAt === 'string'
              ? storeCompany.createdAt
              : new Date().toISOString(),
            usersCount: usersArray.length,
          }];
        } else {
          // Sinon récupérer via authAPI.me()
          const meData = await authAPI.me();
          if (meData.companyId) {
            const companyData = await companiesAPI.getById(meData.companyId);
            if (companyData) {
              companiesArray = [{
                id: companyData.id,
                name: companyData.tradeName || companyData.legalName || 'Ma société',
                siret: companyData.registrationNumber,
                createdAt: typeof companyData.createdAt === 'string'
                  ? companyData.createdAt
                  : new Date().toISOString(),
                usersCount: usersArray.length,
              }];
            }
          }
        }
      } catch (err) {
        logger.warn('Erreur récupération société:', err);
      }

      return {
        users: usersArray,
        companies: companiesArray,
        totalUsers: usersArray.length,
        totalCompanies: companiesArray.length,
        platformVersion: '2.0',
      };
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  const refreshStats = () => {
    queryClient.invalidateQueries({ queryKey: systemOverviewKeys.all });
  };

  return {
    stats: data ?? {
      users: [],
      companies: [],
      totalUsers: 0,
      totalCompanies: 0,
      platformVersion: '2.0',
    },
    isLoading,
    error: error instanceof Error ? error.message : null,
    refreshStats,
    refetch,
  };
}

// ============================================================
// UTILS
// ============================================================
export const formatSystemDate = (date: string): string => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const exportSystemStatsToCSV = (stats: SystemStats): void => {
  const headers = ['Type', 'Nom', 'Email/SIRET', 'Date création'];
  const rows: string[][] = [];

  stats.users.forEach((user) => {
    rows.push(['Utilisateur', user.fullName || '-', user.email, formatSystemDate(user.createdAt)]);
  });

  stats.companies.forEach((company) => {
    rows.push(['Société', company.name, company.siret || '-', formatSystemDate(company.createdAt)]);
  });

  const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `system-overview-${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
};
